import { useState } from 'react'
import { Button, Menu, Modal, Textarea } from '@mantine/core'
import { BOARD_PRIORITY_VALUES, BOARD_STATUS_VALUES } from '@behuman/core-contract'
import { QUEUE_COLOR, STATUS_META, type ValueMeta } from '@/pages/CareCoordination/queueTheme'
import { useUpdateItemValue, type BoardItem, type CellProps } from '../hooks'

const PRIORITY_META: Record<string, ValueMeta> = {
  High: { dot: QUEUE_COLOR.late, color: QUEUE_COLOR.late, weight: 600 },
  Medium: { dot: QUEUE_COLOR.wait, color: QUEUE_COLOR.wait, weight: 500 },
  Low: { dot: QUEUE_COLOR.ringIdle, color: QUEUE_COLOR.muted, weight: 400 },
}

const EMPTY_META: ValueMeta = { dot: '', color: QUEUE_COLOR.subtle, weight: 400 }

// A fax-linked task (PD-1513) needs a completion note the moment it's marked
// Done — the server rejects the write otherwise. Ask for the note up front so
// the user isn't met with a rejected request; an already-completed item is
// never re-prompted.
function needsCompletionNote(item: BoardItem, value: string): boolean {
  return value === 'Done' && !!item.sourceFaxDocumentReferenceId && !item.completion
}

// Status and Priority share this control: a dot plus its label, opening a menu.
// `square` distinguishes priority's mark from status' round one, so the two
// columns stay tellable apart at a glance without a second colour system.
// `requireCompletionNote` is set only by StatusCell — Priority never prompts.
const EnumCell = ({
  item,
  column,
  values,
  meta,
  square,
  requireCompletionNote,
}: CellProps & {
  values: readonly string[]
  meta: Record<string, ValueMeta>
  square?: boolean
  requireCompletionNote?: boolean
}) => {
  const { update, pending } = useUpdateItemValue(item)
  const raw = item.values[column.id]
  const current = typeof raw === 'string' ? raw : null
  const style = current ? (meta[current] ?? EMPTY_META) : EMPTY_META
  const markRadius = square ? '2px' : '50%'

  // Set while the completion-note modal is open for a pending 'Done' pick;
  // cancelling just clears this, leaving the status untouched.
  const [pendingDoneValue, setPendingDoneValue] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const pick = (value: string) => {
    if (requireCompletionNote && needsCompletionNote(item, value)) {
      setPendingDoneValue(value)
      return
    }
    void update(column.id, value)
  }

  const closeModal = () => {
    setPendingDoneValue(null)
    setNote('')
  }

  const confirmCompletion = () => {
    const trimmed = note.trim()
    if (!trimmed || !pendingDoneValue) return
    void update(column.id, pendingDoneValue, trimmed)
    closeModal()
  }

  return (
    <div className="flex w-full items-center justify-start pr-3">
      <Menu shadow="md" position="bottom-start" withinPortal>
        <Menu.Target>
          <button
            type="button"
            aria-label={`${column.label}: ${current ?? 'not set'}`}
            className="flex max-w-full cursor-pointer items-center gap-2 rounded py-1 text-left text-[13.5px] transition hover:opacity-70"
            style={{ color: style.color, fontWeight: style.weight }}
          >
            {style.dot && (
              <span
                className="inline-block h-[7px] w-[7px] shrink-0"
                style={{ background: style.dot, borderRadius: markRadius }}
              />
            )}
            <span className="truncate">{current ?? '—'}</span>
          </button>
        </Menu.Target>
        <Menu.Dropdown>
          {values.map((value) => (
            <Menu.Item key={value} onClick={() => pick(value)}>
              <span className="flex items-center gap-2 text-sm">
                <span
                  className="inline-block h-2.5 w-2.5"
                  style={{
                    background: meta[value]?.dot || QUEUE_COLOR.ringIdle,
                    borderRadius: markRadius,
                  }}
                />
                {value}
              </span>
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item disabled={!current} onClick={() => void update(column.id, null)}>
            <span className="text-sm text-gray-500">Clear</span>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      {requireCompletionNote && (
        <Modal opened={pendingDoneValue !== null} onClose={closeModal} title="Completion note">
          <Textarea
            placeholder="Describe how this fax task was completed…"
            autosize
            minRows={3}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            data-autofocus
          />
          {/* Names the reason the button below is disabled, so an empty note
              doesn't read as a broken dialog. */}
          <p className="mt-1.5 text-xs text-gray-500">Required to complete this fax task.</p>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="subtle" color="gray" onClick={closeModal}>
              Cancel
            </Button>
            <Button disabled={!note.trim()} loading={pending} onClick={confirmCompletion}>
              Complete task
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

export const StatusCell = (props: CellProps) => (
  <EnumCell {...props} values={BOARD_STATUS_VALUES} meta={STATUS_META} requireCompletionNote />
)

export const PriorityCell = (props: CellProps) => (
  <EnumCell {...props} values={BOARD_PRIORITY_VALUES} meta={PRIORITY_META} square />
)
